import { useCallback, useEffect, useRef, useState } from 'react'
import PaymentService from '../../services/payment-service'
import { toast } from 'react-toastify'
import { useMediaQuery } from 'react-responsive'
import { loadStripe } from '@stripe/stripe-js'
import { useMaterialUIController } from 'context'

const usePayment = () => {
  const modalOpen = useRef(false)
  const [subscription, setSubscription] = useState(null)
  const [subscriptionsInfo, setSubscriptionsInfo] = useState(null)
  const [prices, setPrices] = useState(null)
  const [voucher, setVoucher] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const [controller] = useMaterialUIController()
  const { theme } = controller

  const handleCloseModal = useCallback(() => {
    modalOpen.current = false
    setSubscription(null)
  }, [])

  const handleCreateSubscription = useCallback(
    async (priceId, priceAmount, oneTime = false) => {
      modalOpen.current = true
      setSubscription({
        id: oneTime ? 'one-time' : undefined,
        price: priceAmount,
      })
      PaymentService.createSubscription({
        priceId,
        oneTime,
      }).then(res => {
        if (modalOpen.current === true) {
          // If user didn't close the modal yet
          const { subscriptionId, clientSecret, price } = res

          setSubscription({
            id: subscriptionId,
            clientSecret,
            price,
          })
        }
      })
    },
    [],
  )

  const handleLoadSubscriptions = useCallback(() => {
    setPrices(null)
    setIsLoading(true)
    PaymentService.subscriptions(voucher)
      .then(response => {
        setSubscriptionsInfo(response['subscriptionsInfo'])
        const newPrices = response['prices']
        if (newPrices?.exist === true) {
          newPrices.prices.sort((a, b) => {
            if (a.interval_count === b.interval_count) {
              return a.price_value - b.price_value
            }

            if (a.interval_count === undefined) {
              return 1
            }

            if (b.interval_count === undefined) {
              return -1
            }

            return a.interval_count - b.interval_count
          })

          setPrices(newPrices)

          if (voucher) {
            toast.success(`Ai aplicat voucherul ${voucher} !`)
          }
        } else if (voucher) {
          setPrices(newPrices)
          toast.warn('Voucherul nu poate fi aplicat.')
        }
      })
      .finally(() => setIsLoading(false))
  }, [voucher])

  const handleCancelSubscription = useCallback(
    async subscriptionId => {
      setSubscriptionsInfo(null)
      setIsLoading(true)
      PaymentService.cancelSubscription({
        subscription_id: subscriptionId,
      }).then(() => {
        handleLoadSubscriptions()
      })
    },
    [handleLoadSubscriptions],
  )

  const handleContinueSubscription = useCallback(
    async subscriptionId => {
      setSubscriptionsInfo(null)
      setIsLoading(true)
      PaymentService.continueSubscription({
        subscription_id: subscriptionId,
      }).then(() => {
        handleLoadSubscriptions()
      })
    },
    [handleLoadSubscriptions],
  )

  const onPaymentSuccess = async () => {
    // TODO: Add swal alert "Multumim pentru plata"
    setSubscriptionsInfo(null)
    setIsLoading(true)

    setTimeout(handleLoadSubscriptions, 5000)
  }

  const handleShowVoucher = useCallback(() => {
    if (subscriptionsInfo === null) {
      return false
    }
    const keys = Object.keys(subscriptionsInfo)

    if (keys.length === 2) {
      return false
    }
    if (keys.length === 0) {
      return true
    }

    return (
      !keys[0].startsWith('price') &&
      subscriptionsInfo[keys[0]]['custom'] !== true
    )
  }, [subscriptionsInfo])

  const paymentCardsWidth = {
    xl: 3.5,
    lg: 3.7,
  }

  const handleChange = useCallback(event => {
    setVoucher(event.target.value)
  }, [])

  const isNotLarge = useMediaQuery({
    query: '(max-width: 1200px)',
  })

  useEffect(() => {
    handleLoadSubscriptions()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY, {
    locale: 'ro',
  })

  const handleGetStripeOptions = useCallback(() => {
    const appearance = {
      theme: theme === 'dark' ? 'night' : 'stripe',
    }

    if (subscription === null) {
      return {
        mode: 'subscription',
        currency: 'ron',
        amount: 4900,
        appearance,
      }
    }

    return {
      mode: subscription['id'] === 'one-time' ? 'payment' : 'subscription',
      payment_method_types: ['card'],
      currency: 'ron',
      amount: subscription['price'],
      appearance,
    }
  }, [subscription, theme])

  return {
    stripePromise,
    handleGetStripeOptions,
    handleCreateSubscription,
    handleCloseModal,
    handleLoadSubscriptions,
    handleCancelSubscription,
    handleContinueSubscription,
    onPaymentSuccess,
    isNotLarge,
    paymentCardsWidth,
    handleChange,
    handleShowVoucher,
    subscriptionsInfo,
    prices,
    subscription,
    isLoading,
    voucher,
  }
}

export default usePayment
